import { ProgramContentType } from "data/BlockComponent";
import { useRecoilValue } from "recoil";
import AudioButtonWeb from "./audio/AudioButtonWeb";
import { AudioPlayerType, ContentType } from "data/CellComponent";
import useIsInappWebview from "hooks/useIsInappWebview";
import useDetectIOS from "hooks/useDetectIOS";
import { userAtom } from "recoil/userAtom";
import useGetTaskTitleFromTaskKey from "hooks/useGetTaskTitleFromTaskKey";
import { createAudioInstanceKey } from "logic/logics";
import { Box } from "@mui/joy";
import { memo, useMemo } from "react";
import { isAudioPlayingSelector } from "recoil/activeAudioPlayerAtom";
import AudioButtonWebview from "./audio/AudioButtonWebview";

const getMeditationInfo = (audioUrl: string) => {
  const map: Record<string, { title: string; artwork: string }> = {
    stream_of_thoughts: { title: "생각의 강", artwork: "stream.png" },
    sky_of_thoughts: { title: "생각의 하늘", artwork: "sky.png" },
    factory_of_thoughts: { title: "생각의 공장", artwork: "factory.png" },

    body_scan: { title: "바디스캔", artwork: "body_scan.png" },
    observing_body_feelings: { title: "바디스캔", artwork: "body_scan.png" },
    value_wrapup: { title: "가치 확언", artwork: "value_wrapup.png" },
    distancing_thoughtmap: {
      title: "생각 그물",
      artwork: "thought_map.png",
    },
    observing_thoughts: { title: "생각 그물", artwork: "thought_map.png" },
    grounding: { title: "심호흡 그라운딩", artwork: "grounding.png" },
    relaxation: { title: "근육이완법", artwork: "relaxation.png" },
    breath: { title: "호흡 명상", artwork: "breath.png" },
    willingness: { title: "기꺼이 경험하기", artwork: "willingness.png" },
  };

  const key = Object.keys(map).find((k) => audioUrl.startsWith(k));
  return key ? map[key] : undefined;
};

const AudioPlayerCell = ({
  taskKey,
  meditationRecordKey,
  content,
  blockIndex,
  lineIndex,
  cellIndex,
  setData,
  readonly,
}: {
  taskKey: string;
  meditationRecordKey?: string;
  content: ContentType;
  blockIndex: number;
  lineIndex: number;
  cellIndex: number;
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  readonly: boolean;
}) => {
  const { audioUrl, extraAudioUrl, extraLoopNumber, currentTime } =
    content as AudioPlayerType;

  const isInappWebview = useIsInappWebview();
  const { isIOS } = useDetectIOS();

  const user = useRecoilValue(userAtom);
  const isCoach = (user?.accessToken && user?.role !== "patient") || false;

  const taskTitle = useGetTaskTitleFromTaskKey(taskKey, true);

  const audioInstanceKey = useMemo(
    () => createAudioInstanceKey(taskKey, meditationRecordKey, audioUrl),
    [taskKey, meditationRecordKey, audioUrl]
  );

  const meditationInfo = useMemo(() => getMeditationInfo(audioUrl), [audioUrl]);
  const imagePath = `https://media.distancing.im/artwork/meditation/large/${meditationInfo?.artwork || "stream.png"}`;

  const commonButtonProps = useMemo(
    () => ({
      audioInstanceKey,
      audioUrl: `https://media.distancing.im/audio/${audioUrl}_ko.mp3`,
      initialTime: Math.floor(currentTime || 0),
      setData,
      blockIndex,
      lineIndex,
      cellIndex,
      isCoach,
      ...(meditationInfo && {
        title: meditationInfo.title,
        artist: taskTitle,
        artwork: `https://media.distancing.im/artwork/meditation/small/${meditationInfo.artwork}`,
      }),
      ...(extraAudioUrl && {
        extraAudioUrl: `https://media.distancing.im/audio/${extraAudioUrl}_ko.mp3`,
      }),
      extraLoopNumber,
      readonly,
    }),
    [
      audioInstanceKey,
      audioUrl,
      currentTime,
      setData,
      blockIndex,
      lineIndex,
      cellIndex,
      isCoach,
      meditationInfo,
      taskTitle,
      extraAudioUrl,
      extraLoopNumber,
      readonly,
    ]
  );

  const isPlaying = useRecoilValue(isAudioPlayingSelector(audioInstanceKey));

  const mrk = (content as AudioPlayerType).mrk;
  if (mrk !== undefined && mrk !== meditationRecordKey) {
    // TODO 왜 달라지는건지 원인 찾아야함. 우선 방어로직 넣어놓음
    // alert(`${mrk} -> ${meditationRecordKey}`);
    return <></>;
  }

  if (!imagePath) {
    return <div>이미지를 불러올 수 없습니다.</div>;
  }

  return (
    <Box>
      <Box
        sx={{
          width: "100%",
          borderRadius: "8px",
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        <img
          key={`image_${imagePath}`}
          style={{
            width: "100%",
            display: "block",
            transform: isPlaying ? "scale(1.15)" : "scale(1)",
            transition: "transform 1.3s ease-in-out",
          }}
          alt={"meditation_image"}
          src={imagePath}
        />
      </Box>

      {isInappWebview && !isIOS ? (
        <AudioButtonWebview
          key={`button_${audioInstanceKey}`}
          {...commonButtonProps}
        />
      ) : (
        <AudioButtonWeb
          key={`button_${audioInstanceKey}`}
          {...commonButtonProps}
        />
      )}
    </Box>
  );
};

export default memo(AudioPlayerCell);
